/**
 * CRMPage.tsx — Zentrales CRM & Automatisierungsmodul
 * 
 * DESIGN: Mikro-minimalistisch, professionell, Koop-Farbschema
 * DATEN: Echte Supabase-Tabellen (crm_kontakte, crm_vorgaenge, crm_aufgaben, crm_email_vorlagen, crm_kommunikation)
 * GMAIL: Integration über useGmail Hook — E-Mails werden Kontakten zugeordnet
 * 
 * Tabs:
 * 1. Kontakte — Zentrale Kontaktdatenbank mit E-Mail-Historie
 * 2. Pipeline — Kanban-Board für Vorgänge/Fälle
 * 3. Aufgaben & Wiedervorlagen — Pro Kontakt/Fall
 * 4. Automatisierungen — E-Mail-Vorlagen und automatische Aktionen
 */

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation, useRoute } from 'wouter';
import {
  Users, KanbanSquare, ListTodo, Zap, Search, Plus, Phone, Mail, Building2,
  ChevronRight, Clock, AlertCircle, CheckCircle2, ArrowRight, Filter,
  MoreHorizontal, Star, StarOff, Calendar, FileText, Send, Eye, Edit,
  Briefcase, Scale, Wrench, Car, Shield, UserPlus, Tag, X, Trash2,
  GripVertical, RefreshCw, Loader2, Inbox, ArrowUpRight,
} from 'lucide-react';
import {
  useCRMKontakte, useCRMPipeline, useCRMAufgaben, useCRMEmailVorlagen, useCRMKommunikation, useCRMFirmen,
  type CRMKontakt, type CRMVorgang, type CRMAufgabe, type CRMEmailVorlage, type CRMKommunikation, type CRMFirma,
} from '@/hooks/useCRM';
import KontaktDetail from '@/components/KontaktDetail';
import { useGmail, type GmailMessage } from '@/hooks/useGmail';
import { toast } from 'sonner';
import { useMobileDetailBackHandler } from '@/hooks/useMobileBackHandler';
// ============================================================
// TYPES
// ============================================================

type ContactCategory = 'alle' | 'auftraggeber' | 'versicherung' | 'rechtsanwalt' | 'anwalt' | 'werkstatt' | 'haendler' | 'behoerde' | 'kontakt' | 'vermieter' | 'mieter' | 'betrieb' | 'sonstige';
type CRMTab = 'kontakte' | 'firmen' | 'pipeline' | 'aufgaben' | 'automatisierung';

// ============================================================
// CONFIG
// ============================================================

const CATEGORY_CONFIG: Record<ContactCategory, { label: string; icon: typeof Users; color: string }> = {
  alle: { label: 'Alle', icon: Users, color: '#6B7280' },
  auftraggeber: { label: 'Auftraggeber', icon: UserPlus, color: '#2563EB' },
  kontakt: { label: 'Kontakte', icon: Users, color: '#3B82F6' },
  versicherung: { label: 'Versicherungen', icon: Shield, color: '#DC2626' },
  rechtsanwalt: { label: 'Rechtsanwälte', icon: Scale, color: '#7C3AED' },
  anwalt: { label: 'Anwälte', icon: Scale, color: '#7C3AED' },
  werkstatt: { label: 'Werkstätten', icon: Wrench, color: '#D97706' },
  haendler: { label: 'Restwertaufkäufer', icon: Briefcase, color: '#92400E' },
  behoerde: { label: 'Behörden', icon: Building2, color: '#4B5563' },
  vermieter: { label: 'Vermieter', icon: Car, color: '#059669' },
  mieter: { label: 'Mieter', icon: Building2, color: '#0D9488' },
  betrieb: { label: 'Betriebe', icon: Building2, color: '#1D4ED8' },
  sonstige: { label: 'Sonstige', icon: Briefcase, color: '#6B7280' },
};

const TASK_TYPE_CONFIG: Record<string, { label: string; icon: typeof Phone; color: string }> = {
  anruf: { label: 'Anruf', icon: Phone, color: '#2563EB' },
  email: { label: 'E-Mail', icon: Mail, color: '#7C3AED' },
  termin: { label: 'Termin', icon: Calendar, color: '#059669' },
  wiedervorlage: { label: 'Wiedervorlage', icon: Clock, color: '#D97706' },
  aufgabe: { label: 'Aufgabe', icon: ListTodo, color: '#6B7280' },
};

// ============================================================
// HELPERS
// ============================================================

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function isOverdue(dateStr: string): boolean {
  return new Date(dateStr) < new Date(new Date().toDateString());
}

function daysUntil(dateStr: string): number {
  const diff = new Date(dateStr).getTime() - new Date(new Date().toDateString()).getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// ============================================================
// SHARED COMPONENTS
// ============================================================

function StatCard({ label, value, icon: Icon, color }: { label: string; value: number | string; icon: typeof Users; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-2.5 sm:p-3.5 flex items-center gap-2 sm:gap-3 min-w-[120px]">
      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
        <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <div className="text-lg sm:text-xl font-bold text-gray-900">{value}</div>
        <div className="text-[10px] sm:text-[11px] text-gray-500 font-medium truncate">{label}</div>
      </div>
    </div>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const config: Record<string, { label: string; bg: string }> = {
    hoch: { label: 'Hoch', bg: 'bg-red-50 text-red-700 border-red-200' },
    mittel: { label: 'Mittel', bg: 'bg-amber-50 text-amber-700 border-amber-200' },
    niedrig: { label: 'Niedrig', bg: 'bg-gray-50 text-gray-600 border-gray-200' },
  };
  const c = config[priority] || config.mittel;
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${c.bg}`}>{c.label}</span>;
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
    </div>
  );
}

function EmptyState({ icon: Icon, text }: { icon: typeof Users; text: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
      <Icon className="w-10 h-10 mb-2 opacity-40" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

// ============================================================
// MODAL: NEUER KONTAKT
// ============================================================

function NeuerKontaktModal({ open, onClose, onCreate }: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: Partial<CRMKontakt>) => Promise<any>;
}) {
  const [form, setForm] = useState({
    name: '', firma: '', kategorie: 'auftraggeber', email: '', telefon: '', mobil: '', adresse: '', plz: '', ort: '', notizen: '',
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name ist Pflichtfeld'); return; }
    setSaving(true);
    try {
      await onCreate(form);
      toast.success('Kontakt angelegt');
      setForm({ name: '', firma: '', kategorie: 'auftraggeber', email: '', telefon: '', mobil: '', adresse: '', plz: '', ort: '', notizen: '' });
      onClose();
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:h-auto sm:max-w-lg p-6 shadow-xl overflow-y-auto max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Neuer Kontakt</h2>
          <button onClick={onClose} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="Vor- und Nachname" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Firma</label>
              <input value={form.firma} onChange={e => setForm(f => ({ ...f, firma: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="Firmenname" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Kategorie</label>
            <select value={form.kategorie} onChange={e => setForm(f => ({ ...f, kategorie: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
              {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== 'alle').map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">E-Mail</label>
              <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="email@beispiel.de" type="email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Telefon</label>
              <input value={form.telefon} onChange={e => setForm(f => ({ ...f, telefon: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="0151-..." />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">PLZ</label>
              <input value={form.plz} onChange={e => setForm(f => ({ ...f, plz: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="12345" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Ort</label>
              <input value={form.ort} onChange={e => setForm(f => ({ ...f, ort: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="Stadt" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Notizen</label>
            <textarea value={form.notizen} onChange={e => setForm(f => ({ ...f, notizen: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A] h-20 resize-none" placeholder="Optionale Notizen..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Abbrechen</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL: NEUER VORGANG
// ============================================================

function NeuerVorgangModal({ open, onClose, onCreate, kontakte }: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: Partial<CRMVorgang>) => Promise<any>;
  kontakte: CRMKontakt[];
}) {
  const [form, setForm] = useState({ titel: '', kontakt_id: '', stage_name: 'neu', betrieb: 'SV Büro', prioritaet: 'mittel', wert: '', faellig_am: '', notizen: '' });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.titel.trim()) { toast.error('Titel ist Pflichtfeld'); return; }
    setSaving(true);
    try {
      await onCreate({
        ...form,
        wert: form.wert ? parseFloat(form.wert) : undefined,
        kontakt_id: form.kontakt_id || undefined,
        faellig_am: form.faellig_am || undefined,
      } as any);
      toast.success('Vorgang angelegt');
      onClose();
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:h-auto sm:max-w-lg p-6 shadow-xl overflow-y-auto max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Neuer Vorgang</h2>
          <button onClick={onClose} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Titel *</label>
            <input value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="Vorgangstitel" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Kontakt</label>
              <select value={form.kontakt_id} onChange={e => setForm(f => ({ ...f, kontakt_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="">— Kein Kontakt —</option>
                {kontakte.map(k => <option key={k.id} value={k.id}>{k.name}{k.firma ? ` (${k.firma})` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Betrieb</label>
              <select value={form.betrieb} onChange={e => setForm(f => ({ ...f, betrieb: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="SV Büro">SV Büro</option>
                <option value="LCS GmbH">LCS GmbH</option>
                <option value="Unfallgiganten">Unfallgiganten</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Priorität</label>
              <select value={form.prioritaet} onChange={e => setForm(f => ({ ...f, prioritaet: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="hoch">Hoch</option>
                <option value="mittel">Mittel</option>
                <option value="niedrig">Niedrig</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Wert (EUR)</label>
              <input value={form.wert} onChange={e => setForm(f => ({ ...f, wert: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="0.00" type="number" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Fällig am</label>
              <input value={form.faellig_am} onChange={e => setForm(f => ({ ...f, faellig_am: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" type="date" />
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Abbrechen</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// MODAL: NEUE AUFGABE
// ============================================================

function NeueAufgabeModal({ open, onClose, onCreate, kontakte }: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: Partial<CRMAufgabe>) => Promise<any>;
  kontakte: CRMKontakt[];
}) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ titel: '', kontakt_id: '', typ: 'aufgabe', prioritaet: 'mittel', faellig_am: today, beschreibung: '' });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!form.titel.trim()) { toast.error('Titel ist Pflichtfeld'); return; }
    setSaving(true);
    try {
      await onCreate({
        ...form,
        kontakt_id: form.kontakt_id || undefined,
      } as any);
      toast.success('Aufgabe angelegt');
      onClose();
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:h-auto sm:max-w-lg p-6 shadow-xl overflow-y-auto max-h-[85vh]" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Neue Aufgabe</h2>
          <button onClick={onClose} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Titel *</label>
            <input value={form.titel} onChange={e => setForm(f => ({ ...f, titel: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" placeholder="Aufgabentitel" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Kontakt</label>
              <select value={form.kontakt_id} onChange={e => setForm(f => ({ ...f, kontakt_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="">— Kein Kontakt —</option>
                {kontakte.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Typ</label>
              <select value={form.typ} onChange={e => setForm(f => ({ ...f, typ: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                {Object.entries(TASK_TYPE_CONFIG).map(([key, cfg]) => <option key={key} value={key}>{cfg.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Priorität</label>
              <select value={form.prioritaet} onChange={e => setForm(f => ({ ...f, prioritaet: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]">
                <option value="hoch">Hoch</option>
                <option value="mittel">Mittel</option>
                <option value="niedrig">Niedrig</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Fällig am *</label>
              <input value={form.faellig_am} onChange={e => setForm(f => ({ ...f, faellig_am: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" type="date" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Beschreibung</label>
            <textarea value={form.beschreibung} onChange={e => setForm(f => ({ ...f, beschreibung: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A] h-20 resize-none" placeholder="Optionale Beschreibung..." />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5 mobile-dialog-actions sm:pb-0">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Abbrechen</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Anlegen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// KONTAKT-DETAIL PANEL (mit E-Mail-Tab)
// ============================================================

function KontaktDetailPanel({ kontakt, onClose, onUpdate, onToggleFavorit }: {
  kontakt: CRMKontakt;
  onClose: () => void;
  onUpdate: (id: string, updates: Partial<CRMKontakt>) => Promise<any>;
  onToggleFavorit: (id: string) => Promise<void>;
}) {
  const [activeDetailTab, setActiveDetailTab] = useState<'info' | 'emails' | 'notizen'>('info');
  const { kommunikation, loading: komLoading } = useCRMKommunikation(kontakt.id);
  const { connectedEmail, fetchMessages, sendEmail, connect } = useGmail();
  const [gmailMessages, setGmailMessages] = useState<GmailMessage[]>([]);
  const [gmailLoading, setGmailLoading] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);
  // Fetch Gmail messages for this contact's email
  useEffect(() => {
    if (activeDetailTab === 'emails' && connectedEmail && kontakt.email) {
      setGmailLoading(true);
      fetchMessages('INBOX', `from:${kontakt.email} OR to:${kontakt.email}`)
        .then(() => {})
        .catch(() => {})
        .finally(() => setGmailLoading(false));
    }
  }, [activeDetailTab, connectedEmail, kontakt.email]);

  const handleSendEmail = async () => {
    if (!kontakt.email || !composeSubject.trim()) return;
    setSending(true);
    try {
      await sendEmail(kontakt.email, composeSubject, composeBody);
      toast.success('E-Mail gesendet');
      setShowCompose(false);
      setComposeSubject('');
      setComposeBody('');
    } catch (e: any) {
      toast.error(`Fehler beim Senden: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const catCfg = CATEGORY_CONFIG[kontakt.kategorie as ContactCategory] || CATEGORY_CONFIG.sonstige;

  return (
    <div className="w-full lg:w-[380px] flex-shrink-0 bg-white border-t lg:border-t-0 lg:border-l border-gray-200 rounded-xl p-5 overflow-y-auto max-h-[calc(100vh-220px)]">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: catCfg.color }}>
            {getInitials(kontakt.name)}
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">{kontakt.name}</h3>
            {kontakt.firma && <p className="text-sm text-gray-500">{kontakt.firma}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onToggleFavorit(kontakt.id)} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center">
            {kontakt.favorit ? <Star className="w-4 h-4 text-amber-500 fill-amber-500" /> : <Star className="w-4 h-4 text-gray-300" />}
          </button>
          <button onClick={onClose} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Kategorie Badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: `${catCfg.color}15`, color: catCfg.color }}>
          {catCfg.label}
        </span>
        {kontakt.tags?.map(tag => (
          <span key={tag} className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">{tag}</span>
        ))}
      </div>

      {/* Detail Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { id: 'info' as const, label: 'Info', icon: Users },
          { id: 'emails' as const, label: 'E-Mails', icon: Mail },
          { id: 'notizen' as const, label: 'Notizen', icon: FileText },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveDetailTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeDetailTab === tab.id ? 'border-[#C41E3A] text-[#C41E3A]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab: Info */}
      {activeDetailTab === 'info' && (
        <div>
          <div className="space-y-3 mb-5">
            {kontakt.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <a href={`mailto:${kontakt.email}`} className="text-blue-600 hover:underline">{kontakt.email}</a>
              </div>
            )}
            {kontakt.telefon && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <a href={`tel:${kontakt.telefon}`} className="text-gray-700">{kontakt.telefon}</a>
              </div>
            )}
            {kontakt.mobil && (
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{kontakt.mobil} (Mobil)</span>
              </div>
            )}
            {(kontakt.adresse || kontakt.plz || kontakt.ort) && (
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-gray-400" />
                <span className="text-gray-700">{[kontakt.adresse, `${kontakt.plz || ''} ${kontakt.ort || ''}`.trim()].filter(Boolean).join(', ')}</span>
              </div>
            )}
            {kontakt.letzter_kontakt && (
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600">Letzter Kontakt: {formatDate(kontakt.letzter_kontakt)}</span>
              </div>
            )}
          </div>

          {/* Schnellaktionen */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-5">
            <a href={`tel:${kontakt.telefon}`} className="flex flex-col items-center gap-1 p-2.5 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors">
              <Phone className="w-4 h-4 text-blue-600" />
              <span className="text-[10px] font-medium text-blue-700">Anrufen</span>
            </a>
            <button onClick={() => { setActiveDetailTab('emails'); setShowCompose(true); }} className="flex flex-col items-center gap-1 p-2.5 rounded-lg bg-purple-50 hover:bg-purple-100 transition-colors">
              <Mail className="w-4 h-4 text-purple-600" />
              <span className="text-[10px] font-medium text-purple-700">E-Mail</span>
            </button>
            <button className="flex flex-col items-center gap-1 p-2.5 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
              <ListTodo className="w-4 h-4 text-amber-600" />
              <span className="text-[10px] font-medium text-amber-700">Aufgabe</span>
            </button>
          </div>

          {/* Kommunikationshistorie aus Supabase */}
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Kommunikationshistorie</h4>
          {komLoading ? <LoadingSpinner /> : kommunikation.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Noch keine Kommunikation</p>
          ) : (
            <div className="space-y-2">
              {kommunikation.slice(0, 10).map(item => (
                <div key={item.id} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50">
                  {item.typ === 'email' ? <Mail className="w-3.5 h-3.5 text-purple-500 mt-0.5 flex-shrink-0" /> : <Phone className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-700 truncate">{item.betreff || 'Ohne Betreff'}</p>
                    <p className="text-[10px] text-gray-400">
                      {item.richtung === 'eingehend' ? '← Eingehend' : '→ Ausgehend'} · {formatDateTime(item.datum)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: E-Mails (Gmail-Integration) */}
      {activeDetailTab === 'emails' && (
        <div>
          {!connectedEmail ? (
            <div className="text-center py-6">
              <Mail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-3">Gmail nicht verbunden</p>
              <button onClick={() => connect('/crm/kontakte')} className="px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
                Gmail verbinden
              </button>
            </div>
          ) : (
            <div>
              {/* Compose Button */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-gray-500">Verbunden: {connectedEmail}</p>
                <button onClick={() => setShowCompose(!showCompose)} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F] transition-colors">
                  <Send className="w-3 h-3" />
                  Neue E-Mail
                </button>
              </div>

              {/* Compose Form */}
              {showCompose && (
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                  <div className="text-xs text-gray-500">An: {kontakt.email}</div>
                  <input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm" placeholder="Betreff" />
                  <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded text-sm h-24 resize-none" placeholder="Nachricht..." />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowCompose(false)} className="px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-100 rounded">Abbrechen</button>
                    <button onClick={handleSendEmail} disabled={sending} className="flex items-center gap-1 px-3 py-1.5 bg-[#C41E3A] text-white rounded text-xs font-medium disabled:opacity-50">
                      {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Senden
                    </button>
                  </div>
                </div>
              )}

              {/* Gmail Messages */}
              {gmailLoading ? <LoadingSpinner /> : gmailMessages.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Keine E-Mails gefunden für {kontakt.email}</p>
              ) : (
                <div className="space-y-2">
                  {gmailMessages.map(msg => (
                    <div key={msg.id} className="p-2.5 rounded-lg bg-gray-50 border border-gray-100 hover:border-gray-200 transition-colors">
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-xs font-semibold text-gray-800 truncate flex-1">{msg.subject || 'Ohne Betreff'}</p>
                        {msg.isUnread && <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1" />}
                      </div>
                      <p className="text-[10px] text-gray-500 truncate mb-1">{msg.snippet}</p>
                      <p className="text-[10px] text-gray-400">{msg.from} · {formatDateTime(msg.date)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab: Notizen */}
      {activeDetailTab === 'notizen' && (
        <div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{kontakt.notizen || 'Keine Notizen vorhanden.'}</p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: KONTAKTE
// ============================================================

function KontakteTab() {
  const { kontakte, loading, create, update, toggleFavorit, findDuplicates, mergeKontakte } = useCRMKontakte();
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<ContactCategory>('alle');
  const [selectedContact, setSelectedContact] = useState<CRMKontakt | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [displayLimit, setDisplayLimit] = useState(100);

  // Mobile: Hardware-Back schließt Detail-Panel statt Seite zu verlassen
  useMobileDetailBackHandler(!!selectedContact, () => setSelectedContact(null));

  const filtered = useMemo(() => {
    return kontakte.filter(c => {
      if (selectedCategory !== 'alle' && c.kategorie !== selectedCategory) return false;
      if (search) {
        const s = search.toLowerCase();
        return c.name.toLowerCase().includes(s) || c.firma?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.telefon?.includes(s);
      }
      return true;
    });
  }, [kontakte, search, selectedCategory]);

  // Reset display limit when filter changes
  useEffect(() => { setDisplayLimit(100); }, [search, selectedCategory]);
  const displayedContacts = useMemo(() => filtered.slice(0, displayLimit), [filtered, displayLimit]);

  const todayStr = new Date().toISOString().split('T')[0];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      <NeuerKontaktModal open={showNewModal} onClose={() => setShowNewModal(false)} onCreate={create} />

      {/* Kontaktliste */}
      <div className="flex-1 min-w-0">
        {/* Stats - horizontal scrollbar auf Mobile, Grid auf Desktop */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4 sm:grid sm:grid-cols-3 lg:grid-cols-6 sm:gap-3 sm:overflow-visible sm:pb-0 sm:mb-5">
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Gesamt" value={kontakte.length} icon={Users} color="#2563EB" /></div>
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Kontakte" value={kontakte.filter(c => c.kategorie === 'kontakt' || c.kategorie === 'auftraggeber').length} icon={UserPlus} color="#059669" /></div>
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Versicherungen" value={kontakte.filter(c => c.kategorie === 'versicherung').length} icon={Shield} color="#DC2626" /></div>
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Anwälte" value={kontakte.filter(c => c.kategorie === 'anwalt' || c.kategorie === 'rechtsanwalt').length} icon={Scale} color="#7C3AED" /></div>
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Werkstätten" value={kontakte.filter(c => c.kategorie === 'werkstatt').length} icon={Wrench} color="#D97706" /></div>
          <div className="flex-shrink-0 sm:flex-shrink"><StatCard label="Favoriten" value={kontakte.filter(c => c.favorit).length} icon={Star} color="#D97706" /></div>
        </div>

        {/* Suche + Button */}
        <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Suchen..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]"
            />
          </div>
          <button onClick={() => setShowNewModal(true)} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#C41E3A] text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-[#A3172F] transition-colors whitespace-nowrap">
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Neuer Kontakt</span>
            <span className="sm:hidden">Neu</span>
          </button>
        </div>

        {/* Kategorien */}
        <div className="flex gap-1.5 sm:gap-2 overflow-x-auto sm:flex-wrap pb-2 mb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {(Object.entries(CATEGORY_CONFIG) as [ContactCategory, typeof CATEGORY_CONFIG['alle']][]).map(([key, cfg]) => {
            const count = key === 'alle' ? kontakte.length : kontakte.filter(c => c.kategorie === key).length;
            if (key !== 'alle' && count === 0) return null;
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-[11px] sm:text-xs font-medium whitespace-nowrap transition-colors ${
                  selectedCategory === key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <cfg.icon className="w-3 h-3 sm:w-3.5 sm:h-3.5 hidden sm:block" />
                {cfg.label}
                {key !== 'alle' && (
                  <span className="ml-0.5 opacity-60 text-[10px]">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Kontaktliste */}
        <div className="text-xs text-gray-400 mb-2">{filtered.length} Kontakte gefunden{filtered.length > displayLimit ? ` (${displayLimit} angezeigt)` : ''}</div>
        {filtered.length === 0 ? (
          <EmptyState icon={Users} text={kontakte.length === 0 ? 'Noch keine Kontakte — legen Sie den ersten an' : 'Keine Kontakte gefunden'} />
        ) : (
          <div className="space-y-1">
            {displayedContacts.map(contact => {
              const catCfg = CATEGORY_CONFIG[contact.kategorie as ContactCategory] || CATEGORY_CONFIG.sonstige;
              const displayName = contact.name?.replace(/^"+|"+$/g, '').trim() || 'Unbenannt';
              return (
                <button
                  key={contact.id}
                  onClick={() => setSelectedContact(contact)}
                  className={`w-full flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg border transition-all text-left ${
                    selectedContact?.id === contact.id ? 'border-[#C41E3A] bg-red-50/50' : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-white text-[11px] sm:text-sm font-bold flex-shrink-0" style={{ backgroundColor: catCfg.color }}>
                    {getInitials(displayName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span className="text-[13px] sm:text-sm font-semibold text-gray-900 truncate">{displayName}</span>
                      {contact.favorit && <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />}
                      <span className="text-[8px] sm:text-[9px] px-1 sm:px-1.5 py-0.5 rounded-full font-medium hidden sm:inline" style={{ backgroundColor: `${catCfg.color}15`, color: catCfg.color }}>{catCfg.label}</span>
                    </div>
                    {contact.firma && <div className="text-[11px] sm:text-xs text-gray-500 truncate">{contact.firma.replace(/^"+|"+$/g, '')}</div>}
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {contact.email && <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-300" />}
                    {contact.telefon && <Phone className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gray-300" />}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
            {filtered.length > displayLimit && (
              <button
                onClick={() => setDisplayLimit(prev => prev + 100)}
                className="w-full py-3 text-sm font-medium text-[#C41E3A] hover:bg-red-50 rounded-lg transition-colors"
              >
                Weitere {Math.min(100, filtered.length - displayLimit)} von {filtered.length - displayLimit} laden...
              </button>
            )}
          </div>
        )}
      </div>

      {/* Kontakt-Detail Panel (HubSpot-Style) */}
      {selectedContact && (
        <KontaktDetail
          kontakt={selectedContact}
          onClose={() => setSelectedContact(null)}
          onUpdate={update}
          onToggleFavorit={toggleFavorit}
          onFindDuplicates={findDuplicates}
          onMerge={mergeKontakte}
          onSelectKontakt={(k) => setSelectedContact(k)}
        />
      )}
    </div>
  );
}

// ============================================================
// TAB: PIPELINE
// ============================================================

function PipelineTab() {
  const { stages, vorgaenge, loading, createVorgang, moveVorgang, removeVorgang } = useCRMPipeline();
  const { kontakte } = useCRMKontakte();
  const [showNewModal, setShowNewModal] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const STAGE_MAP = useMemo(() => [
    { key: 'neu', label: 'Neu', color: '#3B82F6' },
    { key: 'kontakt', label: 'Kontaktiert', color: '#8B5CF6' },
    { key: 'in_bearbeitung', label: 'In Bearbeitung', color: '#F59E0B' },
    { key: 'warten', label: 'Warten auf Rückmeldung', color: '#EF4444' },
    { key: 'abgeschlossen', label: 'Abgeschlossen', color: '#10B981' },
  ], []);

  const handleDragOver = useCallback((e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverStage(null);
  }, []);

  const handleDrop = useCallback(async (stageName: string) => {
    if (!dragId || moving) return;
    const item = vorgaenge.find(v => v.id === dragId);
    if (item?.stage_name === stageName) {
      setDragId(null);
      setDragOverStage(null);
      return;
    }
    setMoving(true);
    try {
      await moveVorgang(dragId, stageName);
      toast.success(`Vorgang verschoben nach "${STAGE_MAP.find(s => s.key === stageName)?.label}"`);
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    }
    setDragId(null);
    setDragOverStage(null);
    setMoving(false);
  }, [dragId, moving, vorgaenge, moveVorgang, STAGE_MAP]);

  const handleDragEnd = useCallback(() => {
    setDragId(null);
    setDragOverStage(null);
  }, []);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <NeuerVorgangModal open={showNewModal} onClose={() => setShowNewModal(false)} onCreate={createVorgang} kontakte={kontakte} />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5">
        {STAGE_MAP.map(stage => {
          const items = vorgaenge.filter(v => v.stage_name === stage.key);
          const totalValue = items.reduce((s, v) => s + (v.wert || 0), 0);
          return (
            <div key={stage.key} className="bg-white border border-gray-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                <span className="text-xs font-semibold text-gray-700">{stage.label}</span>
              </div>
              <div className="text-xl font-bold text-gray-900">{items.length}</div>
              <div className="text-[10px] text-gray-500">{totalValue.toLocaleString('de-DE')} EUR</div>
            </div>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setShowNewModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
          <Plus className="w-4 h-4" />
          Neuer Vorgang
        </button>
        <p className="text-xs text-gray-400 flex items-center gap-1.5">
          <GripVertical className="w-3.5 h-3.5" />
          Drag & Drop zum Verschieben
        </p>
      </div>

      {/* Kanban Board */}
      <div className="flex flex-col lg:flex-row gap-4 overflow-x-hidden lg:overflow-x-auto pb-4">
        {STAGE_MAP.map(stage => {
          const items = vorgaenge.filter(v => v.stage_name === stage.key);
          const totalValue = items.reduce((s, v) => s + (v.wert || 0), 0);
          const isDropTarget = dragOverStage === stage.key && dragId !== null;
          return (
            <div
              key={stage.key}
              className={`w-full lg:min-w-[260px] lg:w-[260px] flex-shrink-0 rounded-xl p-3 border-2 transition-all duration-200 ${
                isDropTarget
                  ? 'border-dashed bg-blue-50/50 scale-[1.02]'
                  : 'border-transparent bg-gray-50/50'
              }`}
              style={isDropTarget ? { borderColor: stage.color } : undefined}
              onDragOver={e => handleDragOver(e, stage.key)}
              onDragLeave={handleDragLeave}
              onDrop={() => handleDrop(stage.key)}
            >
              <div className="flex items-center gap-2 mb-3 px-1">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                <span className="text-sm font-semibold text-gray-800">{stage.label}</span>
                <div className="ml-auto flex items-center gap-2">
                  {totalValue > 0 && (
                    <span className="text-[10px] font-medium text-gray-400">{totalValue.toLocaleString('de-DE')} EUR</span>
                  )}
                  <span className="text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${stage.color}20`, color: stage.color }}>{items.length}</span>
                </div>
              </div>
              <div className="space-y-2 min-h-[100px]">
                {items.map(item => {
                  const kontaktName = item.kontakt?.name || '—';
                  const isDragging = dragId === item.id;
                  return (
                    <div
                      key={item.id}
                      draggable
                      onDragStart={(e) => {
                        setDragId(item.id);
                        e.dataTransfer.effectAllowed = 'move';
                        if (e.currentTarget instanceof HTMLElement) {
                          e.dataTransfer.setDragImage(e.currentTarget, 50, 20);
                        }
                      }}
                      onDragEnd={handleDragEnd}
                      className={`bg-white border rounded-xl p-3 transition-all duration-200 cursor-grab active:cursor-grabbing ${
                        isDragging
                          ? 'opacity-40 scale-95 border-blue-300 shadow-lg rotate-1'
                          : 'border-gray-200 hover:shadow-md hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900 leading-tight">{item.titel}</h4>
                        <PriorityBadge priority={item.prioritaet} />
                      </div>
                      <p className="text-xs text-gray-500 mb-2">{kontaktName}</p>
                      <div className="flex items-center justify-between">
                        {item.betrieb && <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{item.betrieb}</span>}
                        {item.wert != null && item.wert > 0 && (
                          <span className="text-xs font-semibold text-gray-700">{Number(item.wert).toLocaleString('de-DE')} EUR</span>
                        )}
                      </div>
                      {item.faellig_am && (
                        <div className={`flex items-center gap-1 mt-2 text-[10px] ${isOverdue(item.faellig_am) ? 'text-red-600' : 'text-gray-400'}`}>
                          <Clock className="w-3 h-3" />
                          {isOverdue(item.faellig_am) ? 'Überfällig' : `Fällig: ${formatDate(item.faellig_am)}`}
                        </div>
                      )}
                    </div>
                  );
                })}
                {items.length === 0 && (
                  <div className={`text-center py-8 text-xs border-2 border-dashed rounded-xl transition-colors duration-200 ${
                    isDropTarget ? 'border-blue-300 text-blue-400 bg-blue-50/30' : 'border-gray-200 text-gray-300'
                  }`}>
                    {isDropTarget ? 'Hier ablegen' : 'Keine Vorgänge'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// TAB: AUFGABEN & WIEDERVORLAGEN
// ============================================================

function AufgabenTab() {
  const { aufgaben, loading, create, toggleErledigt, remove } = useCRMAufgaben();
  const { kontakte } = useCRMKontakte();
  const [showErledigt, setShowErledigt] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const sortedTasks = useMemo(() => {
    return [...aufgaben]
      .filter(t => showErledigt || !t.erledigt)
      .sort((a, b) => {
        if (a.erledigt !== b.erledigt) return a.erledigt ? 1 : -1;
        const prio: Record<string, number> = { hoch: 0, mittel: 1, niedrig: 2 };
        if ((prio[a.prioritaet] ?? 1) !== (prio[b.prioritaet] ?? 1)) return (prio[a.prioritaet] ?? 1) - (prio[b.prioritaet] ?? 1);
        return new Date(a.faellig_am).getTime() - new Date(b.faellig_am).getTime();
      });
  }, [aufgaben, showErledigt]);

  const todayStr = new Date().toISOString().split('T')[0];
  const todayTasks = aufgaben.filter(t => !t.erledigt && t.faellig_am === todayStr).length;
  const overdueTasks = aufgaben.filter(t => !t.erledigt && isOverdue(t.faellig_am)).length;
  const openTasks = aufgaben.filter(t => !t.erledigt).length;

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <NeueAufgabeModal open={showNewModal} onClose={() => setShowNewModal(false)} onCreate={create} kontakte={kontakte} />

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <StatCard label="Heute fällig" value={todayTasks} icon={Clock} color="#2563EB" />
        <StatCard label="Überfällig" value={overdueTasks} icon={AlertCircle} color="#DC2626" />
        <StatCard label="Offen gesamt" value={openTasks} icon={ListTodo} color="#D97706" />
        <StatCard label="Erledigt" value={aufgaben.filter(t => t.erledigt).length} icon={CheckCircle2} color="#059669" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setShowNewModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
          <Plus className="w-4 h-4" />
          Neue Aufgabe
        </button>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={showErledigt} onChange={e => setShowErledigt(e.target.checked)} className="rounded" />
          Erledigte anzeigen
        </label>
      </div>

      {/* Task List */}
      {sortedTasks.length === 0 ? (
        <EmptyState icon={ListTodo} text={aufgaben.length === 0 ? 'Noch keine Aufgaben — legen Sie die erste an' : 'Keine offenen Aufgaben'} />
      ) : (
        <div className="space-y-1">
          {sortedTasks.map(task => {
            const typeCfg = TASK_TYPE_CONFIG[task.typ] || TASK_TYPE_CONFIG.aufgabe;
            const TypeIcon = typeCfg.icon;
            const overdue = !task.erledigt && isOverdue(task.faellig_am);
            const days = daysUntil(task.faellig_am);
            const kontaktName = task.kontakt?.name;

            return (
              <div
                key={task.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                  task.erledigt ? 'bg-gray-50 border-gray-100 opacity-60' : overdue ? 'bg-red-50/50 border-red-200' : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <button onClick={() => toggleErledigt(task.id)} className="flex-shrink-0">
                  {task.erledigt ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-[#C41E3A] transition-colors" />
                  )}
                </button>

                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${typeCfg.color}15` }}>
                  <TypeIcon className="w-3.5 h-3.5" style={{ color: typeCfg.color }} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${task.erledigt ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {task.titel}
                  </div>
                  {kontaktName && <div className="text-xs text-gray-500">{kontaktName}</div>}
                </div>

                <PriorityBadge priority={task.prioritaet} />

                <div className={`text-xs flex-shrink-0 ${overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                  {overdue ? `${Math.abs(days)}d überfällig` : days === 0 ? 'Heute' : days === 1 ? 'Morgen' : `${days}d`}
                </div>

                <button onClick={() => { remove(task.id); toast.success('Aufgabe gelöscht'); }} className="p-1 hover:bg-red-50 rounded flex-shrink-0 opacity-0 group-hover:opacity-100">
                  <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: AUTOMATISIERUNGEN
// ============================================================

function AutomatisierungTab() {
  const { vorlagen, loading } = useCRMEmailVorlagen();
  const [selectedTemplate, setSelectedTemplate] = useState<CRMEmailVorlage | null>(null);

  // Mobile: Hardware-Back schließt Template-Detail
  useMobileDetailBackHandler(!!selectedTemplate, () => setSelectedTemplate(null));

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Vorlagen-Liste */}
      <div className="flex-1">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">E-Mail-Vorlagen</h3>
            <p className="text-sm text-gray-500">Vorlagen für häufige Kommunikation — automatisch personalisiert</p>
          </div>
          <button className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
            <Plus className="w-4 h-4" />
            Neue Vorlage
          </button>
        </div>

        {vorlagen.length === 0 ? (
          <EmptyState icon={FileText} text="Noch keine Vorlagen vorhanden" />
        ) : (
          <div className="space-y-2">
            {vorlagen.map(tpl => (
              <button
                key={tpl.id}
                onClick={() => setSelectedTemplate(tpl)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                  selectedTemplate?.id === tpl.id ? 'border-[#C41E3A] bg-red-50/50' : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-sm font-semibold text-gray-900">{tpl.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{tpl.kategorie}</span>
                  </div>
                  <p className="text-xs text-gray-500 truncate">{tpl.betreff}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {tpl.aktiv ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Aktiv</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Entwurf</span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Automatisierungsregeln */}
        <div className="mt-8">
          <h3 className="text-lg font-bold text-gray-900 mb-2">Automatisierungsregeln</h3>
          <p className="text-sm text-gray-500 mb-4">Automatische Aktionen basierend auf Ereignissen</p>

          <div className="space-y-2">
            {[
              { trigger: 'Neue Akte angelegt', action: 'Auftragsbestätigung an Auftraggeber senden', aktiv: true },
              { trigger: 'Gutachten fertiggestellt', action: 'PDF an Auftraggeber + Rechtsanwalt senden', aktiv: true },
              { trigger: 'Rechnung 14 Tage unbezahlt', action: 'Zahlungserinnerung an Versicherung senden', aktiv: true },
              { trigger: 'Besichtigungstermin in 24h', action: 'Erinnerung an Auftraggeber senden', aktiv: false },
            ].map((rule, i) => (
              <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${rule.aktiv ? 'border-green-200 bg-green-50/50' : 'border-gray-200 bg-gray-50'}`}>
                <Zap className={`w-4 h-4 flex-shrink-0 ${rule.aktiv ? 'text-green-600' : 'text-gray-400'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900">
                    <span className="font-medium">Wenn:</span> {rule.trigger}
                  </div>
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">Dann:</span> {rule.action}
                  </div>
                </div>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${rule.aktiv ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {rule.aktiv ? 'Aktiv' : 'Inaktiv'}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Vorlagen-Vorschau */}
      {selectedTemplate && (
        <div className="w-full lg:w-[380px] flex-shrink-0 bg-white border border-gray-200 rounded-xl p-5 overflow-y-auto">
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">{selectedTemplate.name}</h3>
            <button onClick={() => setSelectedTemplate(null)} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Kategorie</label>
              <p className="text-sm text-gray-700 mt-1">{selectedTemplate.kategorie}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Betreff</label>
              <p className="text-sm text-gray-700 mt-1 font-mono bg-gray-50 p-2 rounded">{selectedTemplate.betreff}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Inhalt</label>
              <p className="text-sm text-gray-700 mt-1 bg-gray-50 p-3 rounded leading-relaxed whitespace-pre-wrap">{selectedTemplate.inhalt}</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Variablen</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {selectedTemplate.variablen?.map(v => (
                  <span key={v} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 font-mono">{`{{${v}}}`}</span>
                ))}
              </div>
            </div>

            <div className="flex gap-2 pt-2 mobile-dialog-actions sm:pb-0">
              <button className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
                <Send className="w-4 h-4" />
                Verwenden
              </button>
              <button className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                <Edit className="w-4 h-4" />
                Bearbeiten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// TAB: FIRMEN (HubSpot-Style Companies)
// ============================================================

const FIRMA_KATEGORIEN: { id: string; label: string; color: string }[] = [
  { id: 'alle', label: 'Alle', color: '#6B7280' },
  { id: 'werkstatt', label: 'Werkstatt', color: '#2563EB' },
  { id: 'versicherung', label: 'Versicherung', color: '#DC2626' },
  { id: 'mietwagen', label: 'Mietwagen', color: '#059669' },
  { id: 'autoglas', label: 'Autoglas', color: '#7C3AED' },
  { id: 'anwalt', label: 'Rechtsanwalt', color: '#D97706' },
  { id: 'sonstige', label: 'Sonstige', color: '#6B7280' },
];

function FirmenTab() {
  const { firmen, loading, create, update, remove } = useCRMFirmen();
  const { kontakte } = useCRMKontakte();
  const [search, setSearch] = useState('');
  const [selectedKategorie, setSelectedKategorie] = useState('alle');
  const [selectedFirma, setSelectedFirma] = useState<CRMFirma | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  // Mobile: Hardware-Back schließt Firmen-Detail
  useMobileDetailBackHandler(!!selectedFirma, () => setSelectedFirma(null));

  // Neue Firma Form
  const [newFirma, setNewFirma] = useState({ name: '', kategorie: 'werkstatt', email: '', telefon: '', webseite: '', adresse: '', plz: '', ort: '', branche: '' });

  const filtered = useMemo(() => {
    return firmen.filter(f => {
      if (selectedKategorie !== 'alle' && f.kategorie !== selectedKategorie) return false;
      if (search) {
        const s = search.toLowerCase();
        return f.name.toLowerCase().includes(s) || f.email?.toLowerCase().includes(s) || f.ort?.toLowerCase().includes(s);
      }
      return true;
    });
  }, [firmen, search, selectedKategorie]);

  const handleCreateFirma = async () => {
    if (!newFirma.name.trim()) { toast.error('Firmenname ist erforderlich'); return; }
    try {
      await create({
        ...newFirma,
        erstellt_am: new Date().toISOString(),
        aktualisiert_am: new Date().toISOString(),
      });
      toast.success('Firma erstellt');
      setShowNewModal(false);
      setNewFirma({ name: '', kategorie: 'werkstatt', email: '', telefon: '', webseite: '', adresse: '', plz: '', ort: '', branche: '' });
    } catch (e: any) {
      toast.error(`Fehler: ${e.message}`);
    }
  };

  // Kontakte pro Firma zählen
  const getKontakteCount = (firmaName: string) => kontakte.filter(k => k.firma === firmaName).length;
  const getFirmaKontakte = (firmaName: string) => kontakte.filter(k => k.firma === firmaName);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      {/* Neue Firma Modal */}
      {showNewModal && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 p-0 sm:p-4">
          <div className="bg-white rounded-none sm:rounded-2xl shadow-xl w-full h-full sm:h-auto sm:max-w-md mx-0 sm:mx-4 p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Neue Firma</h3>
              <button onClick={() => setShowNewModal(false)} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <input value={newFirma.name} onChange={e => setNewFirma(f => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Firmenname *" />
              <select value={newFirma.kategorie} onChange={e => setNewFirma(f => ({ ...f, kategorie: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm">
                {FIRMA_KATEGORIEN.filter(k => k.id !== 'alle').map(k => (
                  <option key={k.id} value={k.id}>{k.label}</option>
                ))}
              </select>
              <input value={newFirma.branche} onChange={e => setNewFirma(f => ({ ...f, branche: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Branche" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input value={newFirma.email} onChange={e => setNewFirma(f => ({ ...f, email: e.target.value }))}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="E-Mail" />
                <input value={newFirma.telefon} onChange={e => setNewFirma(f => ({ ...f, telefon: e.target.value }))}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Telefon" />
              </div>
              <input value={newFirma.webseite} onChange={e => setNewFirma(f => ({ ...f, webseite: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Webseite" />
              <input value={newFirma.adresse} onChange={e => setNewFirma(f => ({ ...f, adresse: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Adresse" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input value={newFirma.plz} onChange={e => setNewFirma(f => ({ ...f, plz: e.target.value }))}
                  className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="PLZ" />
                <input value={newFirma.ort} onChange={e => setNewFirma(f => ({ ...f, ort: e.target.value }))}
                  className="col-span-2 px-3 py-2.5 border border-gray-200 rounded-lg text-sm" placeholder="Ort" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5 mobile-dialog-actions sm:pb-0">
              <button onClick={() => setShowNewModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Abbrechen</button>
              <button onClick={handleCreateFirma} className="px-4 py-2 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F]">Erstellen</button>
            </div>
          </div>
        </div>
      )}

      {/* Firmenliste */}
      <div className="flex-1 min-w-0">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          <StatCard label="Firmen" value={firmen.length} icon={Building2} color="#1B3A5C" />
          <StatCard label="Werkstätten" value={firmen.filter(f => f.kategorie === 'werkstatt').length} icon={Wrench} color="#2563EB" />
          <StatCard label="Versicherungen" value={firmen.filter(f => f.kategorie === 'versicherung').length} icon={Shield} color="#DC2626" />
          <StatCard label="Mietwagen" value={firmen.filter(f => f.kategorie === 'mietwagen').length} icon={Car} color="#059669" />
        </div>

        {/* Suche + Button */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Firma, E-Mail, Ort suchen..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#C41E3A]/20 focus:border-[#C41E3A]" />
          </div>
          <button onClick={() => setShowNewModal(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#C41E3A] text-white rounded-lg text-sm font-medium hover:bg-[#A3172F] transition-colors">
            <Plus className="w-4 h-4" />
            Neue Firma
          </button>
        </div>

        {/* Kategorien */}
        <div className="flex gap-2 mb-4 overflow-x-auto sm:flex-wrap pb-1">
          {FIRMA_KATEGORIEN.map(k => (
            <button key={k.id} onClick={() => setSelectedKategorie(k.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                selectedKategorie === k.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {k.label}
              {k.id !== 'alle' && <span className="ml-0.5 opacity-60">{firmen.filter(f => f.kategorie === k.id).length}</span>}
            </button>
          ))}
        </div>

        {/* Firmenliste */}
        {filtered.length === 0 ? (
          <EmptyState icon={Building2} text={firmen.length === 0 ? 'Noch keine Firmen — legen Sie die erste an' : 'Keine Firmen gefunden'} />
        ) : (
          <div className="space-y-1">
            {filtered.map(firma => {
              const katCfg = FIRMA_KATEGORIEN.find(k => k.id === firma.kategorie) || FIRMA_KATEGORIEN[6];
              const kontakteCount = getKontakteCount(firma.name);
              return (
                <button key={firma.id} onClick={() => setSelectedFirma(firma)}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                    selectedFirma?.id === firma.id ? 'border-[#C41E3A] bg-red-50/50' : 'border-transparent hover:bg-gray-50'
                  }`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: katCfg.color }}>
                    {firma.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{firma.name}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${katCfg.color}15`, color: katCfg.color }}>{katCfg.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                      {firma.ort && <span>{firma.ort}</span>}
                      {kontakteCount > 0 && <span>{kontakteCount} Kontakte</span>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Firma-Detail Panel */}
      {selectedFirma && (
        <div className="w-full lg:w-[400px] flex-shrink-0 bg-white border border-gray-200 rounded-xl p-5 overflow-y-auto max-h-[calc(100vh-220px)]">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-[#1B3A5C] flex items-center justify-center text-white text-lg font-bold">
                {selectedFirma.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selectedFirma.name}</h3>
                {selectedFirma.branche && <p className="text-xs text-gray-500">{selectedFirma.branche}</p>}
              </div>
            </div>
            <button onClick={() => setSelectedFirma(null)} className="p-2 sm:p-1 hover:bg-gray-100 rounded min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 flex items-center justify-center"><X className="w-4 h-4 text-gray-400" /></button>
          </div>

          {/* Firmendaten */}
          <div className="space-y-2.5 mb-5">
            {selectedFirma.email && (
              <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /><a href={`mailto:${selectedFirma.email}`} className="text-blue-600 hover:underline">{selectedFirma.email}</a></div>
            )}
            {selectedFirma.telefon && (
              <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /><span className="text-gray-700">{selectedFirma.telefon}</span></div>
            )}
            {selectedFirma.webseite && (
              <div className="flex items-center gap-2 text-sm"><ArrowUpRight className="w-4 h-4 text-gray-400" /><a href={selectedFirma.webseite.startsWith('http') ? selectedFirma.webseite : `https://${selectedFirma.webseite}`} target="_blank" rel="noopener" className="text-blue-600 hover:underline">{selectedFirma.webseite}</a></div>
            )}
            {(selectedFirma.adresse || selectedFirma.ort) && (
              <div className="flex items-center gap-2 text-sm"><Building2 className="w-4 h-4 text-gray-400" /><span className="text-gray-700">{[selectedFirma.adresse, `${selectedFirma.plz || ''} ${selectedFirma.ort || ''}`.trim()].filter(Boolean).join(', ')}</span></div>
            )}
          </div>

          {/* Zugehörige Kontakte */}
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Kontakte ({getKontakteCount(selectedFirma.name)})</h4>
          {getFirmaKontakte(selectedFirma.name).length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-4">Keine Kontakte zugeordnet</p>
          ) : (
            <div className="space-y-2">
              {getFirmaKontakte(selectedFirma.name).map(k => (
                <div key={k.id} className="flex items-center gap-2.5 p-2.5 rounded-lg bg-gray-50">
                  <div className="w-8 h-8 rounded-full bg-[#1B3A5C] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {getInitials(k.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-gray-800 truncate">{k.name}</p>
                    {k.email && <p className="text-[10px] text-gray-500 truncate">{k.email}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Aktionen */}
          <div className="flex gap-2 mt-5 pt-4 border-t border-gray-100 mobile-dialog-actions sm:pb-0">
            <button className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-[#C41E3A] text-white rounded-lg text-xs font-medium hover:bg-[#A3172F]">
              <Edit className="w-3 h-3" />
              Bearbeiten
            </button>
            <button onClick={() => { remove(selectedFirma.id); setSelectedFirma(null); toast.success('Firma gelöscht'); }}
              className="flex items-center justify-center gap-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-red-600 hover:bg-red-50">
              <Trash2 className="w-3 h-3" />
              Löschen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN CRM PAGE
// ============================================================

const CRM_TABS: { id: CRMTab; label: string; icon: typeof Users }[] = [
  { id: 'kontakte', label: 'Kontakte', icon: Users },
  { id: 'firmen', label: 'Firmen', icon: Building2 },
  { id: 'pipeline', label: 'Pipeline', icon: KanbanSquare },
  { id: 'aufgaben', label: 'Aufgaben & Wiedervorlagen', icon: ListTodo },
  { id: 'automatisierung', label: 'Automatisierungen', icon: Zap },
];

export default function CRMPage() {
  const [, params] = useRoute('/crm/:tab?');
  const [, setLocation] = useLocation();
  const activeTab = (params?.tab as CRMTab) || 'kontakte';

  return (
    <div className="p-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">CRM & Automatisierung</h1>
        <p className="text-sm text-gray-500 mt-1">Kontakte verwalten, Vorgänge steuern, Kommunikation automatisieren</p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-gray-200">
        {CRM_TABS.map(tab => {
          const TabIcon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setLocation(`/crm/${tab.id}`)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                isActive
                  ? 'border-[#C41E3A] text-[#C41E3A]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <TabIcon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'kontakte' && <KontakteTab />}
      {activeTab === 'firmen' && <FirmenTab />}
      {activeTab === 'pipeline' && <PipelineTab />}
      {activeTab === 'aufgaben' && <AufgabenTab />}
      {activeTab === 'automatisierung' && <AutomatisierungTab />}
    </div>
  );
}
